import { CreditCardOutlined } from '@ant-design/icons';
import { gql } from '@apollo/client';
import { useAsyncEffect } from 'ahooks';
import { Button } from 'antd';
import { useMutation } from 'hooks/apollo';
import useCustomer from 'hooks/useCustomer';
import useGlobalContext from 'hooks/useGlobalContext';
import useQueryParams from 'hooks/useQueryParams';
import React from 'react';
import { useHistory } from 'react-router-dom';
import { PaymentStatus, PaymentType } from 'shared/services/prisma/types';

const SETUP_PAYMENT_MUTATION = gql`
  mutation AddPaymentMethod($successUrl: String!, $cancelUrl: String!, $customerId: ID!) {
    addPaymentMethod(successUrl: $successUrl, cancelUrl: $cancelUrl, customerId: $customerId)
  }
`;

const PAY_ORDER_MUTATION = gql`
  mutation ($id: ID!) {
    payOrder(id: $id) {
      id
      paymentStatus
      paymentDate
    }
  }
`;

const PAYMENT_TYPE_MUTATION = gql`
  mutation CustomerPaymentType(
    $id: ID!,
    $input: UpdateCustomerInput!
  ) {
    updateCustomer(
      id: $id,
      input: $input,
    ) {
      id
      paymentType
    }
  }
`;

const PayOrderButton = ({ orderId, customerId }) => {
  const { customer, loading: customerLoading } = useCustomer({ extraFields: 'paymentType' });
  const [addPaymentMethod, { loading: paymentMethodLoading }] = useMutation(SETUP_PAYMENT_MUTATION);
  const [updatePaymentType] = useMutation(PAYMENT_TYPE_MUTATION);
  const [payOrder, { loading: payOrderLoading }] = useMutation(PAY_ORDER_MUTATION);
  const { message } = useGlobalContext();
  const query = useQueryParams();
  const history = useHistory();

  const queryParam = `payOrder${orderId}`;

  const result = query.get(queryParam);

  const onPay = async () => {
    try {
      const res = await payOrder({
        variables: {
          id: orderId,
        },
      });
      if (res.data.payOrder.paymentStatus === PaymentStatus.Paid) {
        message.success('Payment successful');
      } else {
        message.error('Payment failed');
      }
    } catch (e) {
      message.error('Payment failed');
    }
  };

  const onAddPaymentMethod = async () => {
    const { data } = await addPaymentMethod({
      variables: {
        successUrl: `${window.location.href}?${queryParam}=success`,
        cancelUrl: `${window.location.href}?${queryParam}=cancel`,
        customerId,
      },
    });
    window.location.href = data.addPaymentMethod;
  };

  const onClick = async () => {
    if (customer.paymentType === PaymentType.Stripe) {
      await onPay();
    } else {
      await onAddPaymentMethod();
    }
  };

  useAsyncEffect(async () => {
    if (result) {
      if (result === 'success') {
        await updatePaymentType({ variables: { id: customerId, input: { paymentType: PaymentType.Stripe } } });
        await payOrder({ variables: { id: orderId } });
      }
      if (result === 'cancel') {
        message.error('Payment setup cancelled');
      }
      history.replace(history.location.pathname);
    }
  }, [result]);

  const loading = customerLoading || paymentMethodLoading || payOrderLoading;

  return <Button icon={<CreditCardOutlined />} onClick={onClick} loading={loading} type="primary">Pay now</Button>;
};

export default PayOrderButton;
